import React, { useEffect, useState } from 'react';
import { useController } from 'react-hook-form';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { Icon } from '@mui/material';
import Styles from '@/assets/js/Styles';
import DraggableCollapseContainer, {
  DraggableCollapseContainerProps,
} from '../../../../components/DraggableCollapseContainer';
import { Draggable } from '../../../../declarations/Draggable';
import { BlockSpecificPage } from '../../../lib/declarations/BlockSpecificPage';
import { deepCopy } from '../../../../utils/object';
import { useCurrentBlock } from '../../CurrentBlockProvider';
import { AttachmentsBlock } from '../../../../declarations/models/blocks/AttachmentsBlock';
import LinkListItemHeader from '../../../../components/Links/LinkListItemHeader';
import AttachmentsBlockItemContent from './AttachmentsBlockItemContent';
import { BlockItemPathPrefix } from '../../../../declarations/models/BlockItemPathPrefix';
import AttachmentsBlockActions from './AttachmentsBlockActions';
import { useEditorEventHandler, useEditorEventManager } from '../../../lib/eventManager/EditorEventManager';
import { useMediaEditorContext } from '../../../MediaEditor/MediaEditorContext';
import { Api } from '../../../../services/Api';
import { M24MediaModel } from '../../../../declarations/models/M24MediaModel';
import { isM24MediaModel } from '../../../../utils/typeChecks';

function getAttachmentIcon(type: string) {
  let symbol: string;
  switch (type) {
    case 'image':
      symbol = 'image';
      break;
    case 'audio':
      symbol = 'audiotrack';
      break;
    default:
      symbol = 'picture_as_pdf';
  }
  return (
    <Icon sx={{ mx: 1 }}>
      <MaterialSymbol name={symbol} />
    </Icon>
  );
}

export const AttachmentsBlockItem = <T extends AttachmentsBlock['items'][0]>({
  index,
  onReorder,
  onRemove,
  onAdd,
}: Pick<DraggableCollapseContainerProps<T>, 'index' | 'onReorder' | 'onRemove' | 'onAdd'>) => {
  const { blockPath } = useCurrentBlock();
  const itemPathPrefix: BlockItemPathPrefix = `${blockPath}.items.${index}`;
  const eventManager = useEditorEventManager();
  const { setMediaId, setEditorOpen, editorOpen } = useMediaEditorContext();
  const {
    field: { value },
  } = useController<BlockSpecificPage<AttachmentsBlock>, typeof itemPathPrefix>({
    name: itemPathPrefix,
  });
  const item: T = value as T;

  const isM24Media = isM24MediaModel(item);
  const [m24Item, setM24Item] = useState<M24MediaModel | undefined>(isM24Media ? item : undefined);
  const handleEditMedia = () => {
    if (m24Item?.id) {
      setMediaId(m24Item.id);
      setEditorOpen(true);
    }
  };

  useEffect(() => {
    if (isM24Media) {
      if (!editorOpen && item.id) {
        const ctx = Api.getM24MediaResource(item.site_id, item.id);
        ctx
          .fetchDirect(null)
          .then((e) => e && setM24Item(e))
          .finally(ctx.abort);
      }
    } else setM24Item(undefined);
  }, [item, editorOpen, isM24Media]);

  return (
    <DraggableCollapseContainer<T>
      useEventHandler={useEditorEventHandler}
      useEventManager={eventManager}
      unmountOnExit
      index={index}
      onReorder={onReorder}
      onRemove={onRemove}
      onAdd={onAdd}
      type={Draggable.ATTACHMENTS_BLOCK_ITEM}
      value={item}
      color={Styles.Colors.LIGHT_GREY}
      secondaryAction={
        <AttachmentsBlockActions
          onDelete={() => onRemove?.(index)}
          onDuplicate={() => onAdd?.(deepCopy(item), index + 1)}
        />
      }
      headerContent={
        // eslint-disable-next-line react/no-unstable-nested-components
        () => <LinkListItemHeader item={m24Item || value} localItem={item} icon={getAttachmentIcon(item.type)} />
      }>
      <AttachmentsBlockItemContent item={m24Item || item} pathPrefix={itemPathPrefix} onEditMedia={handleEditMedia} />
    </DraggableCollapseContainer>
  );
};

export default AttachmentsBlockItem;
